import React, { Children, cloneElement, PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

const DEFAULT_COLORS = '#000000';
const LEFT_CELL_PADDING = { paddingLeft: 6 };

export const DEFAULT_CELL_STYLE = { whiteSpace: 'nowrap' };
export const BLACK_BORDER = '1px solid black';
export const BORDER_BOTTOM = { borderBottom: BLACK_BORDER };
export const BORDER_TOP = { borderTop: BLACK_BORDER };
export const BORDER_RIGHT = { paddingRight: 10, };
export const BORDER_LEFT = { paddingLeft: 10, };
export const pointsToPixels = (points) => (points * 1.3333333333333333);
export const modernTableStyle = (primary = DEFAULT_COLORS) => ({
  fastStatsTable: {
    table: {
      borderCollapse: 'collapse',
    },
    header: {
      fontFamily: 'Barlow, sans-serif',
      fontWeight: 700,
      fontSize: 13,
      textAlign: 'left',
      color: 'black',
      borderBottom: `2px solid ${primary}`,
      borderBottomColor: `${primary}`,
      marginBottom: 0,
      textTransform: 'uppercase',
      // padding: '5px 0',
    },
    row: {
      color: 'black',
      fontFamily: 'Barlow, sans-serif',
      fontSize: 15,
      backgroundColor: 'transparent',
    },
    cell: {
      borderBottom: '1px solid #CECFDA',
      textAlign: 'left',
      whiteSpace: 'wrap',
      display: 'flex',
      alignItems: 'flex-start',
      color: 'black',
      wrappingDiv: true,
      backgroundColor: 'transparent',
    }
  }
});

export function modernTableProps(primary) {
  let STYLE = modernTableStyle(primary);
  return ({
    style: STYLE.fastStatsTable.table,
    rowClassName: 'no-border-row',
  });
}

class TableHead extends PureComponent {
  static propTypes = {
    stripeFunction: PropTypes.func,
    tableAccent: PropTypes.string,
  };

  static defaultProps = {
    tableAccent: '#FFF',
  };

  render() {
    const { children, headerStyle, tableAccent } = this.props;

    return (
      <thead style={{ ...headerStyle }}>
      {
        Children.map(
          children, (child) => cloneElement(
            child,
            {
              style: { backgroundColor: tableAccent, ...this.props.style },
              isHeader: true,
            }
          )
        )
      }
      </thead>
    );
  }
}

class TableHeadCell extends PureComponent {
  static propTypes = {
    rowStyle: PropTypes.object,
  };

  static defaultProps = {
    isVisible: true,
  };

  render() {
    const {
      children, rowStyle, style, cellStyle, isFirst, standardTheme, isLast, parentTile, isVisible,
      ...rest
    } = this.props;
    const thStyle = { ...DEFAULT_CELL_STYLE, ...style, ...rowStyle };

    let paddingStyle = 3 || style && style.padding;


    if (cellStyle) {
      paddingStyle = style && style.padding || '3px 0px';
    }
    if (cellStyle && isFirst && parentTile) {
      paddingStyle = '20px 0px 3px 0px';
    }
    if (standardTheme && (isFirst || isLast)) {
      paddingStyle = 0;
    }

    if (cellStyle && cellStyle.wrappingDiv) {
      return <th style={rowStyle} {...rest}>
        {isVisible &&
        <div style={{
          ...thStyle,
          padding: paddingStyle,
          marginLeft: (isFirst && cellStyle) || !cellStyle ? 0 : 4,
          marginRight: (isLast && cellStyle) || !cellStyle ? 0 : 4,
        }}>
          {children}
        </div>
        }
      </th>;
    }
    return (
      <th
        {...rest}
        style={{
          ...thStyle,
          padding: paddingStyle,
        }}
      >
        {children}
      </th>
    );
  }
}

class TableBody extends PureComponent {
  static propTypes = {
    stripeFunction: PropTypes.func,
    tableAccent: PropTypes.string,
    rowPadding: PropTypes.number,
  };

  static defaultProps = {
    tableAccent: '#F5F5F5',
  };

  static getBgColor = (ix: number, stripeFunction: Function, tableAccent: string) => {
    let striped = (ix % 2 === 0);

    if (stripeFunction) {
      striped = stripeFunction(ix);
    }

    return striped ? { backgroundColor: tableAccent } : null;
  };

  render() {
    const { children, stripeFunction, tableAccent, rowPadding } = this.props;

    return (
      <tbody>
      {
        Children.map(
          children, (child, ix) =>
            (!child ? null : cloneElement(child, {
              style: { ...TableBody.getBgColor(ix, stripeFunction, tableAccent), ...child.props.style },
              rowPadding,
              key: ix,
            })
            )
        )
      }
      </tbody>
    );
  }
}

class TableRow extends PureComponent {
  static propTypes = {
    rowPadding: PropTypes.number,
    isHeader: PropTypes.bool,
  };

  static defaultProps = {
    rowPadding: 10,
    isHeader: false,
  };

  getPadding = ix => (ix > 0 && !this.props.cellStyle ? LEFT_CELL_PADDING : null);


  render() {
    const { rowPadding, children, style, isHeader, cellStyle, standardTheme } = this.props;

    let bufferCell = null;

    if (rowPadding) {
      if (isHeader) {
        bufferCell = <TableHeadCell rowStyle={{ ...style, width: rowPadding }}/>;
      } else {
        bufferCell = <TableCell rowStyle={{ ...style }}/>;
      }
    }
    let noBorder = {};
    if (standardTheme || cellStyle) noBorder = { borderBottom: 'none' };

    const hasChildren = Children.count(children) > 0;

    return (

      <tr
        className={cx('admin-table-row', this.props.className)}
        style={noBorder}
      >
        {!cellStyle && hasChildren && bufferCell}
        {Children.map(children, (child, ix) => cloneElement(
          child,
          {
            key: ix,
            rowStyle: { ...style, ...this.getPadding(ix) },
            isFirst: ix === 0,
            isLast: ix === Children.count(children) - 1,
          }
        ))}
        {!cellStyle && hasChildren && bufferCell}
      </tr>
    );
  }
}

class TableCell extends PureComponent {
  static propTypes = {
    rowStyle: PropTypes.object
  };

  static defaultProps = {
    isVisible: true,
  };

  render() {
    const { children, rowStyle, style, cellStyle, lastRow, isFirst, isLast, per40, isVisible, ...rest }
      = this.props;

    const cellStyling = {
      padding: cellStyle ? '3px 0px 3px 0px' : 3,
      textAlign: cellStyle ? 'left' : 'center',
      ...style,
      ...rowStyle,
      ...cellStyle,
      marginLeft: (isFirst && cellStyle) || !cellStyle ? 0 : 4,
      marginRight: (isLast && cellStyle) || !cellStyle ? 0 : 4,
      borderBottom: lastRow && cellStyle && !per40 ? '2px solid black' : (cellStyle && cellStyle.borderBottom),
    };

    if (cellStyle && cellStyle.wrappingDiv) {
      return (
        <td {...rest} style={{ verticalAlign: 'bottom' }}>
          {isVisible &&
          <div
            style={{ alignItems: 'center', ...cellStyling, display: 'flex', flexDirection: 'column' }}>
            {children}
          </div>
          }
        </td>
      );
    }

    return <td {...rest} style={cellStyling}>{children}</td>;
  }
}

export default class AdminTable extends PureComponent {
  static thead = TableHead;
  static tbody = TableBody;
  static tr = TableRow;
  static th = TableHeadCell;
  static td = TableCell;

  static propTypes = {
    stripeFunction: PropTypes.func,
    rowPadding: PropTypes.number,
  };

  static defaultProps = {
    rowPadding: 10,
  };

  render() {
    const { rowPadding, className, children, stripeFunction, style, cellStyle, widgetId } = this.props;

    return (
      <table
        style={{ borderCollapse: cellStyle ? 'none' : 'collapse', fontSize: 13, ...style }}
        id={widgetId}
        className={cx(className)}
      >
        {Children.map(children, child => cloneElement(child, { stripeFunction, rowPadding }))}
      </table>
    );
  }
}